<template>
  <ErrorBoundary>
    <div class="text-sm font-medium flex-1">
      <div class="grow">
        <div>
          <label>
            {{ t('product.show.checkout.selectDate') }}
          </label>
        </div>
        <!--   BEWARE: adding the allowedDates props to the datePickerProps obj seems to
             break reactivity and thus break the component   -->

        <Datepicker
          ref="datePickerRef"
          v-model="internalSelectedDate"
          v-bind="datePickerProps"
          :allowed-dates="allowedDays"
          :markers="calendarCellMarkers"
          @open="onOpenCalendar"
          @update-month-year="handleChangeMonthYear"
          @overlay-toggle="onOverlayToggle"
        >
          <template #action-row="{ closePicker }">
            <EventBuyBoxDatePickerNoAvailableDates
              v-if="shouldDisplayNoAvailableEvents"
              @click:request-date="closePicker"
            />
            <EventBuyBoxDatePickerCaptions v-else :disable="['soldOut']" />
          </template>
          <template #input-icon>
            <CalendarIcon class="ml-2 fill-text-700" />
          </template>
          <template #clear-icon>
            <ChevronRightIcon class="mr-4 fill-text-600 rotate-90" />
          </template>
          <template #day="{ day }">
            <div class="mt-1 flex-1 flex items-center justify-center">
              {{ day }}
            </div>
          </template>
          <template #marker="{ marker }">
            <div class="block w-full flex overflow-hidden mt-1.5 gap-0.5">
              <div
                v-for="color in marker.color"
                :key="color"
                class="w-full h-[4px] rounded"
                :style="`background-color:${color}`"
              ></div>
            </div>
          </template>
        </Datepicker>
      </div>
    </div>
  </ErrorBoundary>
</template>

<script lang="ts" setup>
import dayjs from 'dayjs'
import Datepicker from '@vuepic/vue-datepicker'
import type {
  EventCalendarDay,
  EventCalendarDayMap,
  EventDate,
  EventDescription,
} from '~/composables/types/event'
import '@vuepic/vue-datepicker/dist/main.css'
import CalendarIcon from '@/public/icons/calendar.svg?component'
import ChevronRightIcon from '@/public/icons/chevron-right.svg?component'
import EventBuyBoxDatePickerNoAvailableDates from '~/components/event/buybox/EventBuyBoxDatePickerNoAvailableDates.vue'
import EventBuyBoxDatePickerCaptions from '~/components/event/buybox/EventBuyBoxDatePickerCaptions.vue'

const props = defineProps<{
  selectedDate?: EventDate
  selectedTicketsQuantity: number
}>()

const emit = defineEmits(['open-date-picker', 'on-dates-loaded'])

const datePickerRef = ref<typeof Datepicker | undefined>(undefined)
const currentYearMonthToFetch = ref<string>()
const selectedDay = defineModel<EventCalendarDay | undefined>(undefined)
const internalSelectedDate = ref<string | undefined>(undefined)
const currentYearMonthOnCalendar = ref<string | undefined>(undefined)

const eventData = inject('eventData') as EventDescription
const { onResponse, fetchedMonths } = useFetchedMonths()
const { t } = useI18n()
const { isAnyOverlayOpen, onOverlayToggle } = useDatePickerOverlay()

const eventId = computed(() => eventGetters.getId(eventData))

const openDatePicker = () => {
  if (datePickerRef.value) datePickerRef.value.openMenu()
}

defineExpose({
  openDatePicker,
})

const {
  data: calendarDayMap,
  refresh,
  execute: fetchCalendar,
} = dayjs(currentYearMonthToFetch?.value).isValid()
  ? await useKftApiFetch<EventCalendarDayMap>(
      `v1/store/events/${eventId.value}/calendar`,
      {
        query: { month: currentYearMonthToFetch },
        immediate: false,
        server: false,
        watch: false,
        onResponse,
      }
    )
  : { data: null, refresh: null }

const mergedCalendarDayMap = useMergedCalendarDayMap(calendarDayMap)
const calendarCellMarkers = useCalendarCellMarkers(
  mergedCalendarDayMap,
  computed(() => props.selectedTicketsQuantity)
)

const allowedDays = computed(() => {
  if (!mergedCalendarDayMap.value) return []
  return Object.keys(mergedCalendarDayMap.value)
    .filter(
      (day) =>
        mergedCalendarDayMap.value[day]?.dates?.some(
          (date) => date.status === 'OPEN'
        ) && mergedCalendarDayMap.value[day].tickets_left > 0
    )
    .sort()
})

const nextAvailableDate = computed(() => allowedDays.value?.[0] || Date.now())

const nextAvailableDay = computed(() =>
  dayjs(new Date(nextAvailableDate.value)).format('YYYY-MM-DD')
)

const nextAvailableDateMonth = computed(() =>
  dayjs(new Date(nextAvailableDate.value)).format('YYYY-MM')
)

const maxDate = computed(() => eventGetters.getMaxDate(eventData))

const calendarIsPastMaxMonth = computed(() =>
  dayjs
    .tz(currentYearMonthOnCalendar.value)
    .isAfter(dayjs.tz(maxDate.value, 'UTC'))
)

const shouldDisplayNoAvailableEvents = computed(
  () => calendarIsPastMaxMonth.value && !isAnyOverlayOpen.value
)

const datePickerProps = computed(() => ({
  config: { shadowDom: true, keepActionRow: true },
  autoApply: !shouldDisplayNoAvailableEvents.value,
  modelType: 'yyyy-MM-dd',
  format: (date: string) => {
    if (!date) return ''
    return dayjs(date).locale('de').format('ddd DD.MM.YYYY')
  },
  enableTimePicker: false,
  minDate: new Date(nextAvailableDate.value),
  locale: 'de',
  shortcuts: false,
  ui: {
    input: '!text-sm !rounded-lg !py-2',
    menu: 'event-buy-box-calendar',
    calendarCell: 'event-buy-box-calendar-cell',
    // Hides calendar if past event's max_date
    calendar: shouldDisplayNoAvailableEvents.value ? 'hidden' : '',
  },
  monthChangeOnScroll: false,
  position: 'left',
  autoPosition: 'bottom',
  noInput: true,
  maxDate: dayjs.tz(maxDate.value, 'UTC').add(2, 'month').toDate(),
  preventMinMaxNavigation: true,
  yearRange: [dayjs().year(), dayjs.tz(maxDate.value, 'UTC').year()],
  class: 'mt-2',
}))

const shouldFetchMonth = (month: string) => {
  const isNextMonthAfterNextAvailableDate = dayjs(month, 'YYYY-MM').isAfter(
    dayjs(nextAvailableDateMonth.value, 'YYYY-MM')
  )

  const isNextMonthAfterMaxMonth = dayjs(month, 'YYYY-MM').isAfter(
    dayjs(maxDate.value, 'YYYY-MM')
  )

  const isNextMonthFetched = fetchedMonths.value[month]

  return (
    !isNextMonthFetched &&
    isNextMonthAfterNextAvailableDate &&
    !isNextMonthAfterMaxMonth
  )
}

const fetchMonth = async (month: string) => {
  if (shouldFetchMonth(month)) {
    currentYearMonthToFetch.value = month
    await refresh()
  }
}

const setCurrentYearMonth = () => {
  const today = dayjs()

  datePickerRef.value?.setMonthYear({
    month: today.month(),
    year: today.year(),
  })
}

const handleChangeMonthYear = async ({
  year,
  month,
}: {
  year: number
  month: number
}) => {
  const newMonth = dayjs().set('month', month).set('year', year)

  const today = dayjs()

  const isMonthYearIsBeforeTodaysMonth = newMonth.isBefore(today)

  if (isMonthYearIsBeforeTodaysMonth) {
    setCurrentYearMonth()
    return
  }

  if (!newMonth.isValid()) return
  currentYearMonthOnCalendar.value = newMonth.format('YYYY-MM')

  await fetchMonth(newMonth.format('YYYY-MM'))
  await fetchMonth(newMonth.add(1, 'month').format('YYYY-MM'))
}

const selectFirstAvailableDay = () => {
  const firstAvailableDay = allowedDays.value?.[0] || nextAvailableDay.value

  if (!firstAvailableDay) return
  internalSelectedDate.value = firstAvailableDay

  return mergedCalendarDayMap.value[firstAvailableDay]
}

const onOpenCalendar = () => {
  currentYearMonthOnCalendar.value = dayjs(internalSelectedDate.value).format(
    'YYYY-MM'
  )

  emit('open-date-picker')
}

const setupMonths = async () => {
  await fetchCalendar()
  currentYearMonthOnCalendar.value = nextAvailableDateMonth.value
  await fetchMonth(
    dayjs(currentYearMonthOnCalendar.value).add(1, 'month').format('YYYY-MM')
  )
}

const updateDateFromSession = () => {
  if (!process.client) return
  if (!sessionStorage.getItem('filters')) return

  const datesFromSession = JSON.parse(sessionStorage.getItem('filters'))

  if (datesFromSession?.dates && datesFromSession.dates.length > 0) {
    internalSelectedDate.value = datesFromSession.dates[0]
    delete datesFromSession.dates
    sessionStorage.setItem('filters', JSON.stringify(datesFromSession))
  }
}

watchEffect(() => {
  if (!internalSelectedDate.value) return
  selectedDay.value = mergedCalendarDayMap.value[internalSelectedDate.value]
})

onMounted(async () => {
  await setupMonths()
  emit('on-dates-loaded')

  selectFirstAvailableDay()
  updateDateFromSession()
})
</script>

<style lang="postcss" scoped>
@import '~/assets/css/components/event/buybox/EventBuyBoxDatePicker.css';
</style>
